import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { MatDialog } from '@angular/material/dialog';
import { finalize, takeUntil, tap } from 'rxjs/operators';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { Store } from '@ngrx/store';

import { ProductTypes } from '../../../products/product-types';
import { SalesPosition, SalesPositionsByProductType } from 'projects/workspace/src/app/sales-order/models/sales-order-position.model';
import { CommonModalsActionsEnum, DangerModalComponent, PositionGroupModalComponent, WarningModalComponent } from '../../modals-common';
import { SalesOrderApiService } from 'projects/workspace/src/app/sales-order/services/sales-order-api.service';
import { SalesOrderModel } from 'projects/workspace/src/app/sales-order/models/sales-order.model';
import { PositionGroupModel, ProductUnitModel, ResponseModel } from 'projects/workspace/src/app/shared/models';
import {
  selectSalesOrderCurrentState,
  selectSalesOrderPositions,
  selectSalesOrderPositionsGroups
} from 'projects/workspace/src/app/sales-order/store/selectors';
import { AppState } from 'projects/workspace/src/app/store/state/app.state';
import { ActionButtonsService } from '../../../../services/action-buttons.service';
import {
  ActionsReorderingPositionModel,
  InputDropdownTypeEnum,
} from '../../../position-card/position-card-models.model';
import { UIStatesEnum } from '../../../../models';
import { PositionsActionsType } from '../../../ui-components/table/custom-table.enums';
import {
  CHANGE_PARENT_QUANTITY_MODAL_DATA,
  REMOVE_POSITION_MODAL_DATA,
  REMOVE_POSITION_NECESSARILY_MODAL_DATA,
  REMOVE_POSITION_PARENT_MODAL_DATA
} from '../../modals.contsans';
import { SalesOrderListTabsEnum } from 'projects/workspace/src/app/sales-order/enums';
import { PositionGroupsService, ProductUnitApiService } from 'projects/workspace/src/app/shared/services';
import { PositionGroupingEnum, PositionMediaTypeEnum, PriceOptionEnum } from 'projects/workspace/src/app/shared/enums';
import { selectCompanyProfile } from 'projects/workspace/src/app/administration/store/selectors';
import { CompanyProfile } from 'projects/workspace/src/app/administration/models/company-profile.model';
import { PositionMediaFormComponent } from 'projects/workspace/src/app/shared/forms/position-media-form/position-media-form.component';

@Component({
  selector: 'rnpl-sales-order-edit-position-modal',
  templateUrl: './sales-order-edit-position-modal.component.html',
  styleUrls: ['./sales-order-edit-position-modal.component.scss']
})
export class SalesOrderEditPositionModalComponent implements OnInit, OnDestroy {

  public activeTab: 'OVERVIEW'|'BUNDLE_PRODUCTS' = 'OVERVIEW';
  public tabs = [
    {
      tabName: 'OVERVIEW',
      label: 'APP.POSITION'
    },
    {
      tabName: 'BUNDLE_PRODUCTS',
      label: 'BUNDLES.PRODUCTS_IN_BUNDLE'
    }
  ];
  public companyProfile: CompanyProfile;
  public productTypes: typeof ProductTypes = ProductTypes;
  public inputDropdownTypeEnum = InputDropdownTypeEnum;
  public mediaTypeEnum = PositionMediaTypeEnum;
  public positionGroupingEnum = PositionGroupingEnum;
  public position: SalesPosition;
  public allPositions: SalesPosition[];
  public groupPositions: SalesPosition[];
  public positionsByGroup: PositionGroupModel[];
  public rowIndex: number;
  public isReadonly: boolean = false;
  public showControls: boolean = true;
  public positionsByProductType: {[key in ProductTypes]?: any[]};

  public unitsList: string[] = [];
  public unitsListSource: ProductUnitModel[] = [];

  @ViewChild('positionMediaFormComponent', { static: false }) positionMediaFormComponentRef: PositionMediaFormComponent;

  public positionsUpdateRequest$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    public store: Store<AppState>,
    public dialogRef: MatDialogRef<SalesOrderEditPositionModalComponent>,
    private readonly salesOrderApi: SalesOrderApiService,
    private positionGroupsService: PositionGroupsService,
    private readonly dialog: MatDialog,
    public actionButtonsService: ActionButtonsService,
    private productUnitApiService: ProductUnitApiService,
    @Inject(MAT_DIALOG_DATA) public data: {
      currentSalesOrder: SalesOrderModel;
      position: SalesPosition,
      groups: PositionGroupModel[],
    }
  ) {
    this.dialogRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.dialogRef.close();
      }
    });

    this.store.select(selectCompanyProfile)
      .pipe(takeUntil(this.destroy$))
      .subscribe((companyProfile: CompanyProfile) => {
        this.companyProfile = companyProfile;
      });
  }

  ngOnInit() {
    this.position = { ...this.data.position };

    this.getProductUnits();
    this.selectAndHandleOrderState();
    this.trackPositionsChanges();
    this.trackPositionsGroupsChanges();
  }

  public getProductUnits(): void {
    this.productUnitApiService.getUnits$(this.position.productType)
      .pipe(takeUntil(this.destroy$))
      .subscribe((unitsList: ProductUnitModel[]) => {
        this.unitsListSource = unitsList;
        if (!this.position.general) {
          this.unitsList = [this.position.unitType];
          return;
        } else {
          this.unitsList = unitsList.map(u => u.name);
        }
        this.redrawControls();
      });
  }

  private selectAndHandleOrderState() {
    this.store.select(selectSalesOrderCurrentState)
      .pipe(takeUntil(this.destroy$))
      .subscribe((state: UIStatesEnum) => {
        this.isReadonly = state === UIStatesEnum.VIEW;
      });
  }

  private trackPositionsChanges(): void {
    if (this.data.currentSalesOrder.preferences.positionGrouping !== PositionGroupingEnum.PRODUCT_TYPE) { return; }

    this.positionsUpdateRequest$.next(true);

    this.store.select(selectSalesOrderPositions)
      .pipe(
        finalize(() => this.positionsUpdateRequest$.next(false)),
        takeUntil(this.destroy$)
      )
      .subscribe((positions: ResponseModel<SalesPositionsByProductType>) => {
        if (positions) {
          this.positionsByProductType = positions.data;
          this.setupPositionsAndGroups();
        }
      });
  }

  private trackPositionsGroupsChanges(): void {
    if (this.data.currentSalesOrder.preferences.positionGrouping !== PositionGroupingEnum.USER_DEFINED) { return; }

    this.positionsUpdateRequest$.next(true);

    this.store.select(selectSalesOrderPositionsGroups)
      .pipe(
        finalize(() => this.positionsUpdateRequest$.next(false)),
        takeUntil(this.destroy$)
      )
      .subscribe((positionsGroups: PositionGroupModel[]) => {
        this.positionsByGroup = positionsGroups;
        this.setupPositionsAndGroups();
      });
  }

  public setupPositionsAndGroups(): void {
    if (this.data.currentSalesOrder.preferences.positionGrouping === PositionGroupingEnum.PRODUCT_TYPE) {
      this.groupPositions = this.positionsByProductType[this.position.productType];
      this.allPositions = [].concat(Object.values(this.positionsByProductType));
    }
    if (this.data.currentSalesOrder.preferences.positionGrouping === PositionGroupingEnum.USER_DEFINED) {
      this.allPositions = [].concat(...this.positionsByGroup.map(item => item.positions));
      this.groupPositions = this.positionsByGroup
        .find(g => g.positions.some(p => p.id === this.position.id))
        .positions;
    }

    this.rowIndex = this.getRowIndex(this.position);
  }

  public updatePosition(fieldName, fieldValue, positionId = +this.position.id, force = false): void {
    if (fieldName === 'quantity' && !force && this.position.linked && this.position.linked.related) {
      this.dialog.open(WarningModalComponent, {
        data: CHANGE_PARENT_QUANTITY_MODAL_DATA,
      }).afterClosed()
        .subscribe((res: CommonModalsActionsEnum) => {
          if (res === CommonModalsActionsEnum.CONFIRM) {
            this.updatePosition(fieldName, fieldValue, positionId, true);
          }
        });
      return;
    }

    this.salesOrderApi
      .updateSalesOrderPosition(
        positionId,
        { fieldName, fieldValue },
        this.data.currentSalesOrder.id
      )
      .subscribe((response) => {
        this.position = { ...response.data };
        this.getCurrentSalesOrder();
      }, () => {
        if (fieldName === 'unitType') {
          this.redrawControls();
        }
      });
  }

  public redrawControls(): void {
    this.showControls = false;
    setTimeout(() => this.showControls = true);
  }

  private getCurrentSalesOrder(): void {
    this.salesOrderApi.getSalesOrderById(this.data.currentSalesOrder.id)
      // .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  public actionListHandler({actionType, row, moveTo, returnData}: ActionsReorderingPositionModel): void {
    if (actionType === PositionsActionsType.INFO) {
      this.actionButtonsService.displaySidebarHandler(row.productId);
      return;
    }
    if (actionType === PositionsActionsType.TO_GROUP) {
      if (!returnData) {
        // create group and move position to it
        this.dialog.open(PositionGroupModalComponent, {
          data: {
            submitRequest$: (groupName: string) => {
              return this.positionGroupsService.createGroup(groupName, 'so',  this.data.currentSalesOrder.id)
                .pipe(tap((group: PositionGroupModel) => this.updatePosition('groupId', group.id)));
            }
          },
        });
        return;
      }
      // move to existed group
      this.updatePosition('groupId', returnData.groupId);
      return;
    }
    this.movePosition(row.id, moveTo);
  }

  public displayProductInfo(): void {
    this.actionButtonsService.displaySidebarHandler(this.position.productId);
  }

  public deletePositions(ids: number[]): void {
    this.salesOrderApi.deleteSalesOrderPositions(this.data.currentSalesOrder.id, ids)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.getCurrentSalesOrder();
        this.dialogRef.close();
      });
  }

  public deletePositionDialog(): void {
    let data = REMOVE_POSITION_MODAL_DATA;

    if (this.position.linked && this.position.linked.related) {
      data = REMOVE_POSITION_PARENT_MODAL_DATA;
    }

    if (this.position.necessarily) {
      data = REMOVE_POSITION_NECESSARILY_MODAL_DATA;
    }

    const dialog = this.dialog.open(DangerModalComponent, { data });

    dialog.afterClosed().subscribe((res) => {
      if (res === CommonModalsActionsEnum.CONFIRM) {
        this.deletePositions([this.position.id]);
      }
    });
  }

  private movePosition(posId: number, moveTo: number): void {
    this.salesOrderApi.changePositionOrder(posId, moveTo)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.position = { ...data };
        this.getCurrentSalesOrder();
      });
  }

  public getRowIndex(position): number {
    let rowIndex: number;
    this.groupPositions.map((pos, index) => {
      if (pos.id === position.id) {
       rowIndex = index;
      }
    });
    return rowIndex;
  }

  public changePosition({position}): void {
    this.position = { ...position };
    this.setupPositionsAndGroups();
    if (this.position.mediaType === PositionMediaTypeEnum.TEXT) {
      this.positionMediaFormComponentRef.resetTextEditorValue(this.position.description);
    }
    this.getCurrentRowIndex();
    this.getProductUnits();
    this.redrawControls();
  }

  public deleteMediaPositionFile(positionId: number): void {
    this.salesOrderApi.deleteMediaPositionFile(this.data.currentSalesOrder.id, positionId)
      .subscribe(() => {
        this.getCurrentSalesOrder();
        this.position = {
          ...this.position,
          fileUrl: null
        };
      });
  }

  public updateMediaPositionFile(positionId: number, event: { file: File; width: number; height: number }): void {
    this.salesOrderApi.updateMediaPositionFile(this.data.currentSalesOrder.id, positionId, event.file, event.width, event.height)
      .subscribe((groups: PositionGroupModel[]) => {
        this.getCurrentSalesOrder();
        const allPositions = [].concat(...groups.map(item => item.positions));
        this.position = { ...allPositions.find((p) => p.id === this.position.id) };
      }, () => {
        this.positionMediaFormComponentRef.resetImg();
        this.positionMediaFormComponentRef.resetImgInput();
      });
  }

  public getCurrentRowIndex(): void {
    this.rowIndex = this.groupPositions
      .findIndex(pos => pos.order === this.position.order && pos.positionId === this.position.positionId);
  }

  get allowFractionalValues(): boolean {
    if (!this.position.unitType || !this.unitsListSource.length) { return true; }
    const selectedUnit: ProductUnitModel = this.unitsListSource
      .find((itm: ProductUnitModel) => itm.name === this.position.unitType);
    const isFractionalQuantity: boolean = !!(this.position.quantity % 1);
    return (selectedUnit && !isFractionalQuantity)
      ? selectedUnit.allowFractionalValues
      : isFractionalQuantity;
  }

  get isGoods(): boolean {
    return this.position.productType === ProductTypes.GOODS;
  }
  get isServices(): boolean {
    return this.position.productType === ProductTypes.SERVICES;
  }
  get isDigital(): boolean {
    return this.position.productType === ProductTypes.DIGITAL;
  }
  get isBundle(): boolean {
    return this.position.productType === ProductTypes.BUNDLE;
  }
  get isCredit(): boolean {
    return this.position.productType === ProductTypes.CREDIT;
  }
  get isGeneral(): boolean {
    return this.position.general;
  }

  get priceOptionNet(): boolean {
    return this.data.currentSalesOrder.preferences.priceOption === PriceOptionEnum.NET
      || this.data.currentSalesOrder.properties.smallBusiness;
  }
  get priceOptionGross(): boolean {
    return this.data.currentSalesOrder.preferences.priceOption === PriceOptionEnum.GROSS
      && !this.data.currentSalesOrder.properties.smallBusiness;
  }

  get soDraft(): boolean {
    return this.data.currentSalesOrder.status === SalesOrderListTabsEnum.DRAFT;
  }
  get soOpen(): boolean {
    return this.data.currentSalesOrder.status === SalesOrderListTabsEnum.OPEN;
  }
  get soCompleted(): boolean {
    return this.data.currentSalesOrder.status === SalesOrderListTabsEnum.COMPLETED;
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

}
